import { useEffect, useRef, useState } from 'react'
import type { StickerDTO } from '../../services/api'
import { mediaUrl } from '../../services/config'

interface Props {
  sticker: { fileUrl: string; fileFormat: string; width: number; height: number; emoji?: string | null } | StickerDTO
  /** Display size in CSS pixels (square). */
  size?: number
}

/**
 * Renders one sticker. WebP / WebM render natively. TGS (gzipped
 * Lottie JSON) requires the lottie-web library to be installed in the
 * runtime; if it's missing we degrade to a static placeholder so the
 * UI doesn't crash. To enable animated TGS:
 *
 *   1. `npm i lottie-web` inside the web container (host npm install
 *      doesn't propagate when Vite runs in Docker).
 *   2. Restart the web dev server.
 *
 * After the package is available the placeholder is replaced live by
 * the dynamic import below.
 */
export default function StickerView({ sticker, size = 128 }: Props) {
  const fmt = (sticker.fileFormat || 'webp').toLowerCase()
  // Resolve the server-relative /uploads path to an absolute URL in native
  // shells (Capacitor/Electron); no-op in the browser. See config.mediaUrl.
  const url = mediaUrl(sticker.fileUrl)
  if (fmt === 'tgs') return <LottieSticker url={url} size={size} title={sticker.emoji || ''} />
  if (fmt === 'webm') {
    return (
      <video
        src={url}
        autoPlay
        loop
        muted
        playsInline
        style={{ width: size, height: size, objectFit: 'contain' }}
      />
    )
  }
  // webp / png / fallback
  return (
    <img
      src={url}
      alt={sticker.emoji || ''}
      style={{ width: size, height: size, objectFit: 'contain' }}
      draggable={false}
    />
  )
}

interface LottieProps {
  url: string
  size: number
  title: string
}

function LottieSticker({ url, size, title }: LottieProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    let cancelled = false
    let anim: { destroy: () => void } | null = null

    ;(async () => {
      try {
        // Telegram .tgs is a gzipped JSON Lottie file. Browsers can't
        // ungzip arbitrary bytes natively so we use DecompressionStream
        // (available in modern Chromium/Firefox/Safari).
        const r = await fetch(url)
        if (!r.ok) throw new Error(`tgs fetch ${r.status}`)
        const stream = (r.body as ReadableStream).pipeThrough(new DecompressionStream('gzip'))
        const text = await new Response(stream).text()
        const json = JSON.parse(text)
        if (cancelled || !containerRef.current) return
        const lottieMod: any = await import('lottie-web')
        const lottie = lottieMod.default ?? lottieMod
        anim = lottie.loadAnimation({
          container: containerRef.current,
          renderer: 'svg',
          loop: true,
          autoplay: true,
          animationData: json,
        })
      } catch (err) {
        console.warn('[LottieSticker] falling back to placeholder', url, err)
        if (!cancelled) setError(true)
      }
    })()

    return () => {
      cancelled = true
      anim?.destroy()
    }
  }, [url])

  if (error) {
    return (
      <div
        title={title || 'sticker (animated)'}
        style={{ width: size, height: size }}
        className="flex items-center justify-center text-muted-foreground text-xs bg-muted/50 rounded"
      >
        🎞️
      </div>
    )
  }

  return <div ref={containerRef} style={{ width: size, height: size }} title={title} />
}
